import { STRAPI_URL, strapiAuthHeaders } from './strapi';

export type FormQuestionOptionSchema = {
  documentId: string;
  title: string;
  answer_type: string;
  sort_order: number;
  start?: number | null;
  end?: number | null;
};

export type FormQuestionSchema = {
  documentId: string;
  form_question_name: string;
  title: string;
  answer_type: string;
  has_option: boolean;
  required: boolean;
  sort_order: number;
  default_value?: string;
  options: FormQuestionOptionSchema[];
};

export type SamplingFormGroup = {
  form_question_name: string;
  questions: FormQuestionSchema[];
};

/** @deprecated Use SamplingFormGroup; kept so existing helpers still type-check. */
export type JenisPengukuranFormGroup = SamplingFormGroup & {
  jenis_pengukuran?: {
    documentId: string;
    nama_jenis: string;
    kode_jenis: string;
  };
};

export type ExistingFormAnswerRow = {
  form_question_document_id: string;
  form_question_option_document_id?: string;
  value: string;
};

export type FieldParameterInput = {
  documentId: string;
  nama_parameter: string;
  simbol: string;
  value: string;
};

export type FieldParameterDraftState = Record<string, string>;

export function fieldParameterDraftFromList(rows: FieldParameterInput[]): FieldParameterDraftState {
  const out: FieldParameterDraftState = {};
  for (const row of rows) {
    const id = row.documentId.trim();
    if (!id) continue;
    out[id] = row.value;
  }
  return out;
}

export const ENVIRONMENT_CONDITION_OPTIONS = [
  'Temperature',
  'Relative Humidity',
  'Pressure',
  'Wind Speed (Average)',
  'Wind Direction',
  'Weather',
] as const;

export type EnvironmentalConditionDraft = {
  nama_parameter: string;
  unit: string;
  result: string;
};

/** Satu baris per opsi. Baris yang dihapus petugas tidak ikut tersimpan. */
export function defaultEnvironmentalConditionDraft(): EnvironmentalConditionDraft[] {
  return ENVIRONMENT_CONDITION_OPTIONS.map((nama_parameter) => ({
    nama_parameter,
    unit: '',
    result: '',
  }));
}

export const VEHICLE_DETAIL_FIELDS = [
  { key: 'fuel_type', label: 'Fuel Type', testId: 'survey-berita-acara-finish-fuel-type' },
  { key: 'hour_meter', label: 'Hour Meter', testId: 'survey-berita-acara-finish-hour-meter' },
  { key: 'production_year', label: 'Production Year', testId: 'survey-berita-acara-finish-production-year' },
  { key: 'vehicle_id', label: 'Vehicle ID/No', testId: 'survey-berita-acara-finish-vehicle-id' },
  { key: 'vehicle_type', label: 'Vehicle Type', testId: 'survey-berita-acara-finish-vehicle-type' },
] as const;

export type VehicleDetailKey = (typeof VEHICLE_DETAIL_FIELDS)[number]['key'];

export type VehicleDetailParameter = Record<VehicleDetailKey, string>;

export function emptyVehicleDetailParameter(): VehicleDetailParameter {
  return {
    fuel_type: '',
    hour_meter: '',
    production_year: '',
    vehicle_id: '',
    vehicle_type: '',
  };
}

export function parseVehicleDetailParameter(raw: unknown): VehicleDetailParameter {
  const out = emptyVehicleDetailParameter();
  const rec = asRecord(raw);
  if (!rec) return out;
  for (const field of VEHICLE_DETAIL_FIELDS) {
    const value = rec[field.key];
    if (value == null || typeof value === 'object') continue;
    out[field.key] = String(value).trim();
  }
  return out;
}

export type LetterSamplingDetail = {
  location_name: string;
  location_address: string;
  weather_condition: string;
  wind_direction_condition: string;
  water_temperature: string;
  dhl: string;
  water_debit: string;
  air_temperature: string;
  salinity: string;
  water_clarity: string;
  ph: string;
  tds: string;
  dissolved_oxygen: string;
  jumlah_sampel: string;
  is_having_blanko_wadah_penyerap: boolean;
  abnormalitas_note: string;
  keterangan_foto: string;
  location_sketsa_picture: string;
  lhu_grouped_name: string;
  air_group: 'air' | 'non_air';
  longitude: number | null;
  latitude: number | null;
  vehicle_information: VehicleDetailParameter;
};

export type BeritaAcaraSamplingFormResponse = {
  form_groups: SamplingFormGroup[];
  jenis_groups: SamplingFormGroup[];
  existing_answers: ExistingFormAnswerRow[];
  letter_detail: LetterSamplingDetail;
  field_parameters: FieldParameterInput[];
  environmental_condition: EnvironmentalConditionDraft[];
};

export type FormAnswerSamplingPayload = {
  form_question_document_id: string;
  form_question_option_document_id?: string;
  value: string;
};

function asRecord(v: unknown): Record<string, unknown> | null {
  return v != null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function mapOption(raw: unknown): FormQuestionOptionSchema | null {
  const o = asRecord(raw);
  const documentId = String(o?.documentId ?? '').trim();
  if (!documentId) return null;
  return {
    documentId,
    title: String(o?.title ?? '').trim(),
    answer_type: String(o?.answer_type ?? 'text').trim(),
    sort_order: Number(o?.sort_order) || 0,
    start: asOptionalInt(o?.start),
    end: asOptionalInt(o?.end),
  };
}

function mapQuestion(raw: unknown): FormQuestionSchema | null {
  const q = asRecord(raw);
  const documentId = String(q?.documentId ?? '').trim();
  if (!documentId) return null;
  const optionsRaw = Array.isArray(q?.options) ? q.options : [];
  const options = optionsRaw
    .map(mapOption)
    .filter((o): o is FormQuestionOptionSchema => o != null)
    .sort((a, b) => a.sort_order - b.sort_order || a.title.localeCompare(b.title, 'id'));
  return {
    documentId,
    form_question_name: String(q?.form_question_name ?? '').trim(),
    title: String(q?.title ?? '').trim(),
    answer_type: String(q?.answer_type ?? 'text').trim(),
    has_option: options.length > 0,
    required: q?.required !== false,
    sort_order: Number(q?.sort_order) || 0,
    default_value: String(q?.default_value ?? '').trim(),
    options,
  };
}

function mapEnvironmentalCondition(raw: unknown): EnvironmentalConditionDraft | null {
  const row = asRecord(raw);
  const nama = String(row?.nama_parameter ?? '').trim();
  if (!(ENVIRONMENT_CONDITION_OPTIONS as readonly string[]).includes(nama)) return null;
  return {
    nama_parameter: nama,
    unit: String(row?.unit ?? '').trim(),
    result: String(row?.result ?? '').trim(),
  };
}

function mapFieldParameter(raw: unknown): FieldParameterInput | null {
  const row = asRecord(raw);
  const documentId = String(row?.documentId ?? '').trim();
  const nama = String(row?.nama_parameter ?? '').trim();
  if (!documentId || !nama) return null;
  return {
    documentId,
    nama_parameter: nama,
    simbol: String(row?.simbol ?? '').trim(),
    value: String(row?.value ?? ''),
  };
}

function mapFormGroup(raw: unknown): SamplingFormGroup | null {
  const g = asRecord(raw);
  if (!g) return null;
  const jp = asRecord(g.jenis_pengukuran);
  const formQuestionName = String(
    g.form_question_name ?? jp?.nama_jenis ?? jp?.kode_jenis ?? '',
  ).trim();
  if (!formQuestionName) return null;
  const questionsRaw = Array.isArray(g.questions) ? g.questions : [];
  const questions = questionsRaw
    .map(mapQuestion)
    .filter((q): q is FormQuestionSchema => q != null)
    .sort((a, b) => a.sort_order - b.sort_order || a.title.localeCompare(b.title, 'id'));
  return { form_question_name: formQuestionName, questions };
}

export function parseBeritaAcaraSamplingFormBody(body: unknown): BeritaAcaraSamplingFormResponse {
  const root = asRecord(body);
  const data = asRecord(root?.data) ?? root;
  const formGroupsRaw = Array.isArray(data?.form_groups) ? data.form_groups : [];
  const jenisGroupsRaw = Array.isArray(data?.jenis_groups) ? data.jenis_groups : [];
  const groupsRaw = formGroupsRaw.length ? formGroupsRaw : jenisGroupsRaw;
  const answersRaw = Array.isArray(data?.existing_answers) ? data.existing_answers : [];
  const fieldParametersRaw = Array.isArray(data?.field_parameters) ? data.field_parameters : [];
  const environmentalRaw = Array.isArray(data?.environmental_condition)
    ? data.environmental_condition
    : [];
  const detail = asRecord(data?.letter_detail);
  const form_groups = groupsRaw
    .map(mapFormGroup)
    .filter((g): g is SamplingFormGroup => g != null && g.questions.length > 0);
  return {
    form_groups,
    jenis_groups: form_groups,
    letter_detail: {
      location_name: String(detail?.location_name ?? '').trim(),
      location_address: String(detail?.location_address ?? '').trim(),
      weather_condition: String(detail?.weather_condition ?? '').trim(),
      wind_direction_condition: String(detail?.wind_direction_condition ?? '').trim(),
      water_temperature: String(detail?.water_temperature ?? '').trim(),
      dhl: String(detail?.dhl ?? '').trim(),
      water_debit: String(detail?.water_debit ?? '').trim(),
      air_temperature: String(detail?.air_temperature ?? '').trim(),
      salinity: String(detail?.salinity ?? '').trim(),
      water_clarity: String(detail?.water_clarity ?? '').trim(),
      ph: String(detail?.ph ?? '').trim(),
      tds: String(detail?.tds ?? '').trim(),
      dissolved_oxygen: String(detail?.dissolved_oxygen ?? '').trim(),
      jumlah_sampel: String(detail?.jumlah_sampel ?? '').trim(),
      is_having_blanko_wadah_penyerap: Boolean(detail?.is_having_blanko_wadah_penyerap),
      abnormalitas_note: String(detail?.abnormalitas_note ?? ''),
      keterangan_foto: String(detail?.keterangan_foto ?? '').trim(),
      location_sketsa_picture: String(detail?.location_sketsa_picture ?? '').trim(),
      lhu_grouped_name: String(detail?.lhu_grouped_name ?? '').trim(),
      air_group: detail?.air_group === 'air' ? 'air' : 'non_air',
      longitude:
        detail?.longitude != null && Number.isFinite(Number(detail.longitude))
          ? Number(detail.longitude)
          : null,
      latitude:
        detail?.latitude != null && Number.isFinite(Number(detail.latitude))
          ? Number(detail.latitude)
          : null,
      vehicle_information: parseVehicleDetailParameter(detail?.vehicle_information),
    },
    field_parameters: fieldParametersRaw
      .map(mapFieldParameter)
      .filter((row): row is FieldParameterInput => row != null),
    environmental_condition: environmentalRaw
      .map(mapEnvironmentalCondition)
      .filter((row): row is EnvironmentalConditionDraft => row != null),
    existing_answers: answersRaw
      .map((row) => {
        const r = asRecord(row);
        if (!r) return null;
        const qId = String(r.form_question_document_id ?? '').trim();
        if (!qId) return null;
        const optId = String(r.form_question_option_document_id ?? '').trim();
        const out: ExistingFormAnswerRow = {
          form_question_document_id: qId,
          value: String(r.value ?? ''),
        };
        if (optId) out.form_question_option_document_id = optId;
        return out;
      })
      .filter((r): r is ExistingFormAnswerRow => r != null),
  };
}

export async function strapiGetBeritaAcaraSamplingForm(
  jwt: string,
  beritaAcaraDocumentId: string,
  jenisPengukuranDocumentId: string,
): Promise<BeritaAcaraSamplingFormResponse> {
  const params = new URLSearchParams();
  const jenisId = String(jenisPengukuranDocumentId ?? '').trim();
  if (jenisId) params.set('jenis_pengukuran_document_id', jenisId);
  const qs = params.toString();
  const res = await fetch(
    `${STRAPI_URL}/api/berita-acara-letters/${encodeURIComponent(beritaAcaraDocumentId)}/sampling-form${
      qs ? `?${qs}` : ''
    }`,
    { headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) {
    const err = asRecord(body);
    const errNested = asRecord(err?.error);
    const msg = String(errNested?.message ?? err?.message ?? `HTTP ${res.status}`);
    throw new Error(msg);
  }
  return parseBeritaAcaraSamplingFormBody(body);
}

export type FormAnswerDraftState = Record<string, string>;

export function formAnswerDraftKey(questionDocumentId: string, optionDocumentId?: string): string {
  return optionDocumentId ? `${questionDocumentId}:${optionDocumentId}` : questionDocumentId;
}

export function formAnswerRangeDraftKey(
  questionDocumentId: string,
  optionDocumentId: string,
  rangeValue: number,
): string {
  return `${formAnswerDraftKey(questionDocumentId, optionDocumentId)}#${rangeValue}`;
}

export function optionIsRange(option: { answer_type?: string | null }): boolean {
  return String(option.answer_type ?? '').trim().toLowerCase() === 'range';
}

export const FORM_OPTION_RANGE_MAX_SPAN = 48;

function asOptionalInt(v: unknown): number | null {
  if (v == null || v === '') return null;
  if (typeof v === 'string' && !v.trim()) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isInteger(n) ? n : null;
}

export function parseOptionRangeBounds(option: {
  start?: unknown;
  end?: unknown;
}): { start: number; end: number } | null {
  const start = asOptionalInt(option.start);
  const end = asOptionalInt(option.end);
  if (start == null || end == null) return null;
  if (end < start) return null;
  if (end - start + 1 > FORM_OPTION_RANGE_MAX_SPAN) return null;
  return { start, end };
}

export function applyFormQuestionOptionRangeWrite(data: Record<string, unknown>): Record<string, unknown> {
  const type = String(data.answer_type ?? '').trim().toLowerCase();
  if (type !== 'range') {
    return { ...data, start: null, end: null };
  }
  const bounds = parseOptionRangeBounds({ start: data.start, end: data.end });
  if (!bounds) {
    throw new Error('Range wajib: start dan end bilangan bulat, start ≤ end, dan span maksimal 48.');
  }
  return { ...data, start: bounds.start, end: bounds.end };
}

export function optionRangeValues(option: {
  answer_type?: string | null;
  start?: number | null;
  end?: number | null;
}): number[] {
  if (!optionIsRange(option)) return [];
  const bounds = parseOptionRangeBounds(option);
  if (!bounds) return [];
  const out: number[] = [];
  for (let n = bounds.start; n <= bounds.end; n += 1) out.push(n);
  return out;
}

export function rangeColumnKey(optionDocumentId: string, rangeValue: number): string {
  return `${optionDocumentId}#${rangeValue}`;
}

export function parseRangeColumnValue(columnKey: string): number | null {
  const i = columnKey.lastIndexOf('#');
  if (i <= 0) return null;
  const n = Number(columnKey.slice(i + 1));
  return Number.isInteger(n) ? n : null;
}

export function isSerializedRangeValues(raw: string): boolean {
  const t = raw.trim();
  if (!t.startsWith('{')) return false;
  try {
    const parsed = JSON.parse(t) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false;
    const entries = Object.entries(parsed as Record<string, unknown>);
    if (!entries.length) return false;
    return entries.every(([key, value]) => {
      if (!/^-?\d+$/.test(key)) return false;
      return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
    });
  } catch {
    return false;
  }
}

export function parseRangeAnswerValues(raw: string): Record<string, string> {
  if (!isSerializedRangeValues(raw)) return {};
  const parsed = JSON.parse(raw.trim()) as Record<string, unknown>;
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(parsed)) {
    out[key] = String(value ?? '');
  }
  return out;
}

export type FormAnswerFileRef = {
  name: string;
  url: string;
};

export function formAnswerFileFieldKey(questionDocumentId: string, optionDocumentId?: string): string {
  return optionDocumentId ? `${questionDocumentId}__${optionDocumentId}` : questionDocumentId;
}

export function parseFormAnswerFileFieldKey(key: string): {
  questionDocumentId: string;
  optionDocumentId?: string;
} {
  const i = key.indexOf('__');
  if (i <= 0) return { questionDocumentId: key };
  const optionDocumentId = key.slice(i + 2).trim();
  return {
    questionDocumentId: key.slice(0, i),
    optionDocumentId: optionDocumentId || undefined,
  };
}

export function isFormAnswerFileRef(raw: string): boolean {
  const t = raw.trim();
  if (!t.startsWith('{')) return false;
  try {
    const parsed = JSON.parse(t) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false;
    const rec = parsed as Record<string, unknown>;
    return Boolean(String(rec.url ?? '').trim() && String(rec.name ?? rec.url ?? '').trim());
  } catch {
    return false;
  }
}

export function parseFormAnswerFileRef(raw: string): FormAnswerFileRef | null {
  if (!isFormAnswerFileRef(raw)) return null;
  const parsed = JSON.parse(raw.trim()) as Record<string, unknown>;
  const url = String(parsed.url ?? '').trim();
  const name = String(parsed.name ?? '').trim() || url.split('/').pop() || url;
  return { name, url };
}

export function serializeFormAnswerFileRef(ref: FormAnswerFileRef): string {
  return JSON.stringify({ name: ref.name, url: ref.url });
}

export function serializeRangeAnswerValues(
  option: FormQuestionOptionSchema,
  draft: FormAnswerDraftState,
  questionDocumentId: string,
): string {
  const obj: Record<string, string> = {};
  for (const n of optionRangeValues(option)) {
    const value = (draft[formAnswerRangeDraftKey(questionDocumentId, option.documentId, n)] ?? '').trim();
    if (value) obj[String(n)] = value;
  }
  return Object.keys(obj).length ? JSON.stringify(obj) : '';
}

/** True when the question has a non-empty options list. `has_option` alone can be stale. */
export function questionHasOptions(question: { options?: unknown[] | null }): boolean {
  return Array.isArray(question.options) && question.options.length > 0;
}

function questionCellAnswerType(question: FormQuestionSchema, option?: FormQuestionOptionSchema): string {
  const parent = String(question.answer_type ?? '').trim().toLowerCase();
  if (!option) return parent;
  if (optionIsRange(option)) {
    if (
      parent === 'number' ||
      parent === 'decimal' ||
      parent === 'text' ||
      parent === 'boolean' ||
      parent === 'date' ||
      parent === 'file'
    ) {
      return parent;
    }
    return 'number';
  }
  if (parent === 'date' || parent === 'file') return parent;
  return String(option.answer_type || question.answer_type || 'text').trim().toLowerCase();
}

function booleanDraftValue(raw: string): string | null {
  const normalized = raw.trim().toLowerCase();
  if (normalized === 'true' || normalized === 'ya' || normalized === 'yes' || normalized === '1') return 'true';
  if (normalized === 'false' || normalized === 'tidak' || normalized === 'no' || normalized === '0') return 'false';
  return null;
}

function draftValueForAnswerType(answerType: string, fallback: string): string | null {
  if (answerType === 'file') return null;
  if (answerType === 'boolean') return booleanDraftValue(fallback);
  return fallback;
}

function applyQuestionDefault(draft: FormAnswerDraftState, question: FormQuestionSchema, fallback: string): void {
  if (!questionHasOptions(question)) {
    const value = draftValueForAnswerType(questionCellAnswerType(question), fallback);
    if (value == null) return;
    draft[formAnswerDraftKey(question.documentId)] = value;
    return;
  }

  const parent = questionCellAnswerType(question);
  if (parent === 'single') {
    const match = question.options.find(
      (opt) =>
        !optionIsRange(opt) && opt.title.trim().toLocaleLowerCase('id') === fallback.toLocaleLowerCase('id'),
    );
    if (!match) return;
    draft[formAnswerDraftKey(question.documentId, match.documentId)] = 'true';
    return;
  }

  for (const opt of question.options) {
    if (optionIsRange(opt)) {
      const value = draftValueForAnswerType(questionCellAnswerType(question, opt), fallback);
      if (value == null) continue;
      for (const n of optionRangeValues(opt)) {
        draft[formAnswerRangeDraftKey(question.documentId, opt.documentId, n)] = value;
      }
      continue;
    }
    const value = draftValueForAnswerType(questionCellAnswerType(question, opt), fallback);
    if (value == null) continue;
    draft[formAnswerDraftKey(question.documentId, opt.documentId)] = value;
  }
}

/** Existing saved answers win. Master `default_value` fills a question only when it has no saved answer yet. */
export function buildSamplingFormAnswerDraft(
  groups: SamplingFormGroup[],
  answers: ExistingFormAnswerRow[],
): FormAnswerDraftState {
  const draft = buildFormAnswerDraftFromExisting(answers);
  const answered = new Set(answers.map((row) => row.form_question_document_id));
  for (const group of groups) {
    for (const question of group.questions) {
      if (answered.has(question.documentId)) continue;
      const fallback = String(question.default_value ?? '').trim();
      if (!fallback) continue;
      applyQuestionDefault(draft, question, fallback);
    }
  }
  return draft;
}

export function buildFormAnswerDraftFromExisting(answers: ExistingFormAnswerRow[]): FormAnswerDraftState {
  const out: FormAnswerDraftState = {};
  for (const row of answers) {
    const value = row.value;
    if (row.form_question_option_document_id && isSerializedRangeValues(value)) {
      const parsed = parseRangeAnswerValues(value);
      for (const [key, cell] of Object.entries(parsed)) {
        out[formAnswerRangeDraftKey(row.form_question_document_id, row.form_question_option_document_id, Number(key))] =
          cell;
      }
      continue;
    }
    out[formAnswerDraftKey(row.form_question_document_id, row.form_question_option_document_id)] = value;
  }
  return out;
}

export function buildFormAnswersPayload(
  groups: SamplingFormGroup[],
  draft: FormAnswerDraftState,
  pendingFiles: Record<string, File> = {},
): FormAnswerSamplingPayload[] {
  const out: FormAnswerSamplingPayload[] = [];
  for (const group of groups) {
    for (const question of group.questions) {
      if (questionHasOptions(question)) {
        for (const opt of question.options) {
          if (optionRangeValues(opt).length) {
            const value = serializeRangeAnswerValues(opt, draft, question.documentId);
            if (value) {
              out.push({
                form_question_document_id: question.documentId,
                form_question_option_document_id: opt.documentId,
                value,
              });
            }
            continue;
          }
          const draftKey = formAnswerDraftKey(question.documentId, opt.documentId);
          const fileKey = formAnswerFileFieldKey(question.documentId, opt.documentId);
          const value = (draft[draftKey] ?? '').trim();
          if (value || pendingFiles[fileKey]) {
            out.push({
              form_question_document_id: question.documentId,
              form_question_option_document_id: opt.documentId,
              value,
            });
          }
        }
      } else {
        const draftKey = formAnswerDraftKey(question.documentId);
        const fileKey = formAnswerFileFieldKey(question.documentId);
        const value = (draft[draftKey] ?? '').trim();
        if (value || pendingFiles[fileKey]) {
          out.push({
            form_question_document_id: question.documentId,
            value,
          });
        }
      }
    }
  }
  return out;
}

export function validateFormAnswerDraft(
  groups: SamplingFormGroup[],
  draft: FormAnswerDraftState,
): string | null {
  for (const group of groups) {
    for (const question of group.questions) {
      if (!question.required) continue;
      if (questionHasOptions(question)) {
        for (const opt of question.options) {
          const range = optionRangeValues(opt);
          if (range.length) {
            for (const n of range) {
              const value = (
                draft[formAnswerRangeDraftKey(question.documentId, opt.documentId, n)] ?? ''
              ).trim();
              if (!value) {
                return `Jawaban wajib: ${question.title} — ${opt.title} ${n}`;
              }
            }
            continue;
          }
          const value = (draft[formAnswerDraftKey(question.documentId, opt.documentId)] ?? '').trim();
          if (!value) {
            return `Jawaban wajib: ${question.title} — ${opt.title}`;
          }
        }
      } else {
        const value = (draft[formAnswerDraftKey(question.documentId)] ?? '').trim();
        if (!value && question.answer_type !== 'boolean') {
          return `Jawaban wajib: ${question.title}`;
        }
      }
    }
  }
  return null;
}

export function formQuestionTabLabel(formQuestionName: string): string {
  return formQuestionName.trim() || 'Form sampling';
}
